/**
 * Note control curves `posControl`, `alphaControl`, `sizeControl`, `yControl` (W1-05a engine-eval;
 * rpe-format.md F§12, R§2.4, F§11.4).
 *
 * A control is a keyframe track over the note's distance key h (RPE-y px, see `controlKey`), evaluated with the
 * ordinary Anim rules (f32 values, f32 lerp, extrapolation before the first point, the last value held after the
 * last one). EACH POINT'S EASING GOVERNS THE INTERVAL THAT ENDS AT IT: keyframe i uses the tween of point i + 1's
 * easing (`RPE_TWEEN_MAP`, no slice, no bezier); the first point's easing is unused.
 *
 * Phira's inert rule: a control is ignored entirely (factor 1) when it has no points, or exactly two points whose
 * FIRST point has easing 1 and value within 1e-4 of 1 (the second point is not checked, so RPE's default
 * `[{x:0,easing:1,v:1},{x:9999999,easing:1,v:1}]` and also `[{x:0,easing:1,v:1},{x:500,easing:1,v:0}]` are inert).
 *
 * A note with speed 0 has the key `yOffsetTerm / speed = 0 / 0 = NaN`. Phira's `set_time(NaN)` walks the cursor
 * forward to the last point (`next.x > NaN` never stops it) and never back (`x > NaN` is false), so an active
 * control yields its LAST point's value for that note, whatever was evaluated before (R§2.4). The checker warns
 * about speed-0 notes on lines with distance effects (PH-14).
 *
 * Internal API (W1-05b's `EngineCompileContext.control`, W1-06 notes):
 *   compileControls(irLine)          the four curves of a line (null = absent or inert)
 *   controls.at(which, h)            factor at key h, or null when the control is inert
 *   controlKey(noteH, lineH, yOffset, speed)    Phira's key h in f32
 *   isInertControl(points)
 */

import { rpeToTweenId } from '../ease/rpeMap';
import { HOLD_TWEEN } from '../ease/tweens';
import type { IrControlPoint, IrLine } from '../ir/types';
import { AnimF32 } from './anim';

const fr = Math.fround;

export type ControlWhich = 'pos' | 'alpha' | 'size' | 'y';

const INERT_EPS = fr(1e-4);

/** Phira's inert rule (see the module comment). */
export function isInertControl(points: readonly IrControlPoint[] | undefined): boolean {
  if (points === undefined || points.length === 0) return true;
  if (points.length !== 2) return false;
  const first = points[0];
  if (first === undefined) return true;
  return first.easing === 1 && Math.abs(fr(fr(first.value) - 1)) < INERT_EPS;
}

/** The keyframe track of an active control (keys x in f64, values f32, tween of point i+1 at keyframe i). */
export function controlTrack(points: readonly IrControlPoint[]): AnimF32 {
  const n = points.length;
  const xs: number[] = [];
  const vs: number[] = [];
  const tw: number[] = [];
  for (let i = 0; i < n; i++) {
    const p = points[i];
    const next = points[i + 1];
    xs.push(p?.x ?? Number.NaN);
    vs.push(fr(p?.value ?? Number.NaN));
    tw.push(next === undefined ? HOLD_TWEEN : rpeToTweenId(next.easing));
  }
  return AnimF32.fromKeyframes(xs, vs, tw);
}

/** A compiled control, or null when absent or inert. */
export function compileControl(points: readonly IrControlPoint[] | undefined): AnimF32 | null {
  if (points === undefined || isInertControl(points)) return null;
  return controlTrack(points);
}

export interface LineControls {
  readonly pos: AnimF32 | null;
  readonly alpha: AnimF32 | null;
  readonly size: AnimF32 | null;
  readonly y: AnimF32 | null;
  /** Factor at key h; null when the control is absent or inert (factor 1). */
  at(which: ControlWhich, h: number): number | null;
  /** True when any control is active. */
  readonly any: boolean;
}

export function compileControls(
  line: Pick<IrLine, 'posControl' | 'alphaControl' | 'sizeControl' | 'yControl'>,
): LineControls {
  const pos = compileControl(line.posControl);
  const alpha = compileControl(line.alphaControl);
  const size = compileControl(line.sizeControl);
  const y = compileControl(line.yControl);
  const pick = (which: ControlWhich): AnimF32 | null =>
    which === 'pos' ? pos : which === 'alpha' ? alpha : which === 'size' ? size : y;
  return {
    pos,
    alpha,
    size,
    y,
    any: pos !== null || alpha !== null || size !== null || y !== null,
    at(which: ControlWhich, h: number): number | null {
      const c = pick(which);
      return c === null ? null : c.valueAt(h);
    },
  };
}

/**
 * Phira's control key h (F§11.4, R§2.4), in f32: `(H(note) - H(now) + ty / speed) * 450` with
 * `ty = yOffset * 2 / 900 * speed` (the parser's stored yOffset term). It equals (H(note) - H(now))·450 + yOffset,
 * ignores note speed and yControl, and is NaN for speed 0.
 */
export function controlKey(noteHeight: number, lineHeight: number, yOffset: number, speed: number): number {
  const sp = fr(speed);
  const ty = fr(fr(fr(fr(yOffset) * 2) / 900) * sp);
  return fr(fr(fr(fr(noteHeight) - fr(lineHeight)) + fr(ty / sp)) * 450);
}
